import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import LisualTimelapsePlayer from '../../../lisualTimelapse/components/LisualTimelapsePlayer';
import { generateEditorVideo, getEditorVideoDownloadUrl } from '../../../videoEditor/api/videoEditor.api';
import { getVideoStatus } from '../../../lisualTimelapse/api/videoService.api';
import { getImageRange } from '../../api/iframeConfig.api';
import { getObraByFeedID } from '../../../obras/api/obras.api';
import { getObraTimeZone } from '../../../videoEditor/utils/obraTimeZone';
import TimelapseRapido from '../../../lisualTimelapse/components/TimelapseRapido';
import ProgresoTimelapse from '../../../lisualTimelapse/components/ProgresoTimelapse';
import { leerTimelapseSemanal } from '../../../lisualTimelapse/api/timelapseSemanal.api';
import VivoBeta from '../vivoBeta/VivoBeta';

// "Timelapse de la Obra" con chips de período. "Todos los tiempos" es el
// timelapse del proyecto de siempre (LisualTimelapsePlayer). Los rangos se
// generan con el mismo motor que el Editor de Video (POST /api/video/editor)
// y los mismos parámetros que su modo Básico (fotos de 11 a 14 h, intervalo
// 15 min, sin mezcla de fotos). El resultado queda guardado por cámara + rango
// + día en localStorage, así no se vuelve a generar en el día.
// Nada de esto le avisa al cliente: el editor no manda mails ni WhatsApp.
// Desde el 24/10/2026 cada período sale primero del lote de los sábados (TIMELAPSE-SEMANAL, GET /api/timelapse-semanal):
// el video se ve al instante con la fecha hasta la que llega, y "Quiero el timelapse a hoy" lo arma en el momento como antes.
// Mientras el video no está (se arma, falla o se traba) se ve el "timelapse rápido" del
// período hecho en el navegador con las fotos (TimelapseRapido), con un rótulo chico que dice qué pasa con el video.

const PERIODOS = [
  { id: 'todos', label: 'Todos los tiempos' },
  { id: '90', label: 'Últimos 90 días', days: 90, durationSeconds: 30 },
  { id: '60', label: 'Últimos 60', days: 60, durationSeconds: 30 }, // el motor sólo acepta 5/10/15/30/60/90 s (25 daba FAILED)
  { id: '30', label: 'Últimos 30', days: 30, durationSeconds: 15 },
  { id: '15', label: 'Últimos 15', days: 15, durationSeconds: 15 },
];

const POLL_MS = 4000;
const MAX_POLLS = 240;
// Si el pedido no cambia de estado en este tiempo se da por trabado (se deja de mostrar la barra).
const TRABADO_EN_COLA_MS = 4 * 60 * 1000;
const TRABADO_ARMANDO_MS = 8 * 60 * 1000;
// "A hoy" espera más: el motor del Editor hace un video por vez y puede tener cola; la barra sigue mientras tanto.
const TRABADO_A_HOY_MS = 15 * 60 * 1000;
const trabadoMs = (status, modoHoy) => {
  const sim = process.env.NODE_ENV === 'development' && window.__lisualPreviewTrabadoMs;
  if (sim) return sim;
  if (modoHoy) return TRABADO_A_HOY_MS;
  return status === 'PROCESSING' ? TRABADO_ARMANDO_MS : TRABADO_EN_COLA_MS;
};
const TZ_AR = 'America/Argentina/Buenos_Aires';

// El motor del Editor (BACK-VIDEO-ENGINE) está prendido 24/7 desde el 17/10/2026: no hay horario.
// Si igual falla, se queda el timelapse rápido.

const hoy = () => new Date().toISOString().slice(0, 10);
const cacheKey = (feedId, rango) => `lisual-tl-rango:${feedId}:${rango}`;
const leerCache = (feedId, rango) => {
  try {
    const v = JSON.parse(localStorage.getItem(cacheKey(feedId, rango)));
    return v && v.dia === hoy() ? v : null;
  } catch (e) {
    return null;
  }
};
const guardarCache = (feedId, rango, jobId) => {
  try { localStorage.setItem(cacheKey(feedId, rango), JSON.stringify({ jobId, dia: hoy() })); } catch (e) { /* bloqueado */ }
};
const borrarCache = (feedId, rango) => {
  try { localStorage.removeItem(cacheKey(feedId, rango)); } catch (e) { /* bloqueado */ }
};

const mensajeError = (err) => {
  const txt = String(err?.response?.data?.message || err?.response?.data?.error || err?.message || '');
  if (err?.response?.status === 401 || err?.response?.status === 403) return 'Timelapse rápido · para el video en alta calidad volvé a entrar a LisualPro';
  return 'Timelapse rápido · el video en alta calidad no se pudo armar ahora, probá más tarde';
};

// Cuánto suele tardar el motor del Editor en armar cada período (segundos; para la barra de "a hoy").
const ESTIMADO_SEG = { 15: 30, 30: 40, 60: 60, 90: 80 };
const estimadoDe = (periodo) => ESTIMADO_SEG[periodo.days] || 150;

// modoHoy: "Quiero el timelapse a hoy". En vez del timelapse rápido con el rótulo arriba, se ve el video del sábado
// oscurecido de fondo (fondoUrl) y, en el medio, la barra con el porcentaje y cuánto falta. Si el motor falla
// reintenta solo una vez; si vuelve a fallar avisa con onFallo (se vuelve al video del sábado, nunca una pantalla de error).
// periodo.desdeFijo ('AAAA-MM-DD'): inicio fijo (todos los tiempos = la primera imagen de la obra que eligió el lote).
const RangoTimelapse = ({ feedId, periodo, onListo, modoHoy = false, fondoUrl, onVolver, onFallo }) => {
  const inicioHoy = useRef(Date.now());
  const [intento, setIntento] = useState(0);
  const [estado, setEstado] = useState({ fase: 'inicio' });
  const [videoCargado, setVideoCargado] = useState(false);
  const vivo = useRef(true);
  // Inicio del período del timelapse rápido (todo = desde la primera foto).
  const desdeRapido = useRef(periodo.days ? Date.now() - periodo.days * 864e5 : null);

  const esperar = useCallback(async (jobId) => {
    let ultimo = { status: null, desde: Date.now() };
    for (let i = 0; i < MAX_POLLS && vivo.current; i++) {
      const job = await getVideoStatus(jobId);
      if (!vivo.current) return;
      if (job?.status === 'COMPLETED') {
        const url = job.videoUrl || (await getEditorVideoDownloadUrl(jobId, feedId));
        if (vivo.current) setEstado({ fase: 'listo', url });
        return;
      }
      if (job?.status === 'FAILED') throw new Error('FAILED');
      if (job?.status !== ultimo.status) ultimo = { status: job?.status, desde: Date.now() };
      else if (Date.now() - ultimo.desde > trabadoMs(ultimo.status, modoHoy)) throw new Error('trabado');
      setEstado({ fase: 'generando', status: ultimo.status, desde: ultimo.desde });
      await new Promise((r) => setTimeout(r, POLL_MS));
    }
    if (vivo.current) throw new Error('timeout');
  }, [feedId, modoHoy]);

  useEffect(() => {
    vivo.current = true;
    (async () => {
      const guardado = leerCache(feedId, periodo.id);
      if (guardado?.jobId) {
        setEstado({ fase: 'buscando' });
        try {
          await esperar(guardado.jobId);
          return;
        } catch (e) {
          borrarCache(feedId, periodo.id);
        }
      }
      // (En /__preview-inicio el motor está simulado en src/dev/previewInicioMock.js: nunca pide renders reales.)
      setEstado({ fase: 'generando', status: 'PENDING', desde: Date.now() });
      try {
        const fin = new Date();
        let inicio = periodo.desdeFijo ? new Date(`${periodo.desdeFijo}T03:00:00Z`)
          : periodo.days ? new Date(fin.getTime() - periodo.days * 24 * 3600 * 1000) : null;
        if (!inicio) {
          // Toda la obra: desde la primera foto (igual que hace el asistente).
          const rango = await getImageRange(feedId, { period: 'COMPLETO' }).catch(() => null);
          const primera = rango?.firstImage?.date ? new Date(rango.firstImage.date) : null;
          inicio = primera && !Number.isNaN(primera.getTime()) ? primera : new Date(fin.getTime() - 365 * 24 * 3600 * 1000);
        }
        const form = new FormData();
        form.append('feedId', feedId);
        form.append('period', 'CUSTOM');
        form.append('customStart', inicio.toISOString());
        form.append('customEnd', fin.toISOString());
        form.append('orientation', 'LANDSCAPE_16_9');
        form.append('intervalMinutes', '15');
        form.append('quality', 'FHD');
        form.append('durationSeconds', String(periodo.durationSeconds));
        form.append('timeOfDayStart', '11:00');
        form.append('timeOfDayEnd', '14:00');
        // Camera-site zone from the obra; falls back to the browser zone when the obra has no location.
        const obraTz = await getObraByFeedID(feedId).then(getObraTimeZone).catch(() => null);
        form.append('timeZone', obraTz || Intl.DateTimeFormat().resolvedOptions().timeZone || TZ_AR);
        form.append('temporalBlend', 'false');
        form.append('useClientLogo', 'false');
        const job = await generateEditorVideo(form);
        if (!job?.id) throw new Error('sin id');
        guardarCache(feedId, periodo.id, job.id);
        await esperar(job.id);
      } catch (err) {
        if (!vivo.current) return;
        if (modoHoy && intento === 0) { borrarCache(feedId, periodo.id); setIntento(1); return; } // un reintento, sin mostrar nada
        if (modoHoy && onFallo) { onFallo(); return; }
        setEstado({ fase: 'error', mensaje: mensajeError(err) });
      }
    })();
    return () => { vivo.current = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedId, periodo, esperar, intento]);

  // El video nuevo se precarga oculto y recién se muestra cuando ya se puede
  // reproducir: mientras tanto nunca se ve un video viejo ni a medias.
  const listo = estado.fase === 'listo';
  const avisado = useRef(false);
  useEffect(() => {
    if (listo && videoCargado && onListo && !avisado.current) { avisado.current = true; onListo(); }
  }, [listo, videoCargado, onListo]);
  if (listo && videoCargado) {
    return (
      <video key={estado.url} src={estado.url} controls autoPlay muted playsInline className="w-full aspect-video rounded-xl bg-black" />
    );
  }
  const precarga = listo && (
    <video
      key={`pre-${estado.url}`}
      src={estado.url}
      preload="auto"
      muted
      playsInline
      onLoadedData={() => setVideoCargado(true)}
      onError={() => setVideoCargado(true)}
      className="absolute w-px h-px opacity-0 pointer-events-none"
      aria-hidden="true"
    />
  );
  if (modoHoy) {
    return (
      <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-gray-900" data-tl-a-hoy-progreso>
        {precarga}
        {fondoUrl && <video src={fondoUrl} muted playsInline preload="auto" className="absolute inset-0 h-full w-full object-cover opacity-40" aria-hidden="true" />}
        {estado.fase === 'error' ? (
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-sm rounded-2xl bg-black/60 px-5 py-4 text-center text-sm text-white shadow-xl backdrop-blur-sm">
              <p className="font-semibold">No pudimos armar el timelapse a hoy</p>
              <p className="mt-1 text-xs text-white/75">Probá de nuevo en unos minutos.</p>
              {onVolver && <button type="button" onClick={onVolver} className="mt-3 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold hover:bg-white/25">Volver al timelapse del sábado</button>}
            </div>
          </div>
        ) : (
          <ProgresoTimelapse desde={inicioHoy.current} listo={listo} estimadoSeg={estimadoDe(periodo)} texto="Armando el timelapse con las fotos hasta hoy" nota="" />
        )}
      </div>
    );
  }
  // Sin video semanal (todavía no lo armó el lote): las fotos de la obra de fondo y, en el medio, la misma barra
  // que "a hoy" (porcentaje y cuánto falta). Nunca el rótulo chico de arriba ni "Jugar mientras esperás". Si el motor
  // falla, quedan sólo las fotos, sin cartel.
  const generando = estado.fase === 'generando' || estado.fase === 'buscando' || estado.fase === 'inicio' || listo;
  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-gray-200" data-tl-armando>
      {precarga}
      <TimelapseRapido feedId={feedId} desde={desdeRapido.current} />
      {generando && <ProgresoTimelapse desde={inicioHoy.current} listo={listo} estimadoSeg={estimadoDe(periodo)} texto="Armando el timelapse en alta calidad" nota="" />}
    </div>
  );
};

// El timelapse "de todos los tiempos" que devuelve /api/video/latest es el
// último video guardado en los últimos 90 días, aunque se haya armado con el
// primer día de la obra (caso BD: un video de 3 s del 18/09 para una obra de
// un mes). Si el video dura menos de MIN_TODOS_SEG lo reemplazamos por uno de
// toda la obra hecho con el motor del editor (60 s), cacheado en el día.
const MIN_TODOS_SEG = 15;
const TODOS_COMPLETO = { id: 'todos-completo', label: 'Todos los tiempos', days: null, durationSeconds: 60 };

const TodosLosTiempos = ({ userdata }) => {
  const [corto, setCorto] = useState(null);
  const [fallo, setFallo] = useState(false); // el video de siempre falló: se arma con el motor del Editor
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onMeta = (e) => {
      const d = e.target?.duration;
      if (e.target?.tagName === 'VIDEO' && Number.isFinite(d) && d > 0 && d < MIN_TODOS_SEG) setCorto(d);
    };
    el.addEventListener('loadedmetadata', onMeta, true);
    return () => el.removeEventListener('loadedmetadata', onMeta, true);
  }, []);
  if (fallo || corto != null) {
    return <RangoTimelapse key={`${userdata.feedID}-todos`} feedId={userdata.feedID} periodo={TODOS_COMPLETO} />;
  }
  return (
    <div ref={ref}>
      <LisualTimelapsePlayer user={userdata} className="w-full" onFallo={() => setFallo(true)} />
    </div>
  );
};

// Con tantos cambios de posición (PTZ que se gira) el timelapse queda cortado: se le pide al cliente que no la mueva.
const CAMBIOS_AVISO = 4;

// "con fotos hasta el vie 23/10" (las fechas del lote son días, sin hora: se leen en UTC para no correrse un día)
const fechaCorta = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'numeric', timeZone: 'UTC' });

// Período ya armado el sábado: se ve al instante. Abajo, hasta qué día llega y el botón para armarlo a hoy.
const PeriodoSemanal = ({ feedId, periodo, semanal }) => {
  const [aHoy, setAHoy] = useState('no'); // no | armando | listo | fallo
  const [intentoHoy, setIntentoHoy] = useState(0);
  const p = semanal.periodos[periodo.id];
  // Todos los tiempos a hoy: desde la misma primera imagen de la obra que usó el lote (no la primera foto de S3).
  const rangoAHoy = periodo.id === 'todos' ? { ...TODOS_COMPLETO, desdeFijo: p.desde } : periodo;
  const verSabado = aHoy === 'no' || aHoy === 'fallo';
  // Filtro de encuadre: el video del sábado va sin las fotos en que la cámara apuntaba a otro lado (PTZ, golpe, tapada).
  // Si se sacó alguna, se puede ver el completo.
  const [completo, setCompleto] = useState(false);
  const src = completo && p.completoUrl ? p.completoUrl : p.url;
  return (
    <div>
      {verSabado
        ? <video key={src} src={src} controls autoPlay muted playsInline className="w-full aspect-video rounded-xl bg-black" data-tl-semanal={periodo.id} />
        : <RangoTimelapse key={`${feedId}-${periodo.id}-hoy-${intentoHoy}`} feedId={feedId} periodo={rangoAHoy} modoHoy fondoUrl={p.url} onListo={() => setAHoy('listo')} onVolver={() => setAHoy('no')} onFallo={() => setAHoy('fallo')} />}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600" data-tl-pie>
        <span>
          {aHoy === 'fallo' && <>No pudimos actualizarlo a hoy en este momento; te mostramos el del <b className="text-gray-800">{fechaCorta(p.hasta)}</b>.</>}
          {aHoy === 'no' && <>Timelapse con fotos hasta el <b className="text-gray-800">{fechaCorta(p.hasta)}</b> · se actualiza todos los sábados</>}
          {aHoy === 'armando' && 'Cuando termine, el video a hoy se reproduce solo.'}
          {aHoy === 'listo' && <>Timelapse <b className="text-gray-800">actualizado a hoy</b></>}
        </span>
        {(aHoy === 'no' || aHoy === 'fallo') && (
          <button type="button" onClick={() => { setIntentoHoy((n) => n + 1); setAHoy('armando'); }} className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 font-semibold text-blue-700 hover:bg-blue-100" data-tl-a-hoy>
            {aHoy === 'fallo' ? 'Probar de nuevo' : 'Quiero el timelapse a hoy'}
          </button>
        )}
        {verSabado && p.completoUrl && p.fueraDeCuadro > 0 && (
          <button type="button" onClick={() => setCompleto((v) => !v)} className="font-semibold text-gray-600 underline underline-offset-2 hover:text-gray-900" data-tl-completo>
            {completo
              ? 'Ver timelapse corregido'
              : `Ver timelapse completo (con ${p.fueraDeCuadro === 1 ? 'la foto que sacamos' : `las ${p.fueraDeCuadro} fotos que sacamos`}: fuera de cuadro, lluvia o sin cambios)`}
          </button>
        )}
      </div>
      {verSabado && p.cambiosDePosicion >= CAMBIOS_AVISO && (
        <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-tl-camara-movida>
          <span aria-hidden>📷</span>
          <span>
            <b>Esta cámara cambió de posición {p.cambiosDePosicion} veces en este período</b> y eso corta el timelapse.
            Te recomendamos dejarla fija en una sola posición y no usar los giros de la cámara: así el timelapse de tu obra queda continuo.
          </span>
        </div>
      )}
    </div>
  );
};

const TimelapsePorPeriodo = ({ userdata, onOpenEditor }) => {
  const [periodoId, setPeriodoId] = useState('todos');
  const periodo = PERIODOS.find((p) => p.id === periodoId);
  const [semanal, setSemanal] = useState(null); // null mientras se consulta; { periodos } después (vacío si no hay lote)
  useEffect(() => {
    let vivo = true;
    setSemanal(null);
    leerTimelapseSemanal(userdata.feedID).then((s) => { if (vivo) setSemanal(s || { periodos: {} }); });
    return () => { vivo = false; };
  }, [userdata.feedID]);
  const tieneSemanal = !!semanal?.periodos?.[periodo.id];

  return (
    <div>
      <div className="mb-2 flex gap-1.5 overflow-x-auto pb-1 -mx-0.5 px-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="radiogroup" aria-label="Período del timelapse">
        {PERIODOS.map((p) => (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={periodoId === p.id}
            onClick={() => setPeriodoId(p.id)}
            className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
              periodoId === p.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {/* El botón VIVO (beta) va arriba a la derecha del video, en todas las cámaras */}
      <div className="relative">
        <VivoBeta userdata={userdata} className="absolute top-2 right-2 z-10" />
        {!semanal
          ? <div className="w-full aspect-video rounded-xl bg-gray-200 animate-pulse" aria-label="Cargando el timelapse" />
          : tieneSemanal
            ? <PeriodoSemanal key={`${userdata.feedID}-${periodo.id}-semanal`} feedId={userdata.feedID} periodo={periodo} semanal={semanal} />
            : periodo.id === 'todos'
              ? <TodosLosTiempos userdata={userdata} />
              : <RangoTimelapse key={`${userdata.feedID}-${periodo.id}`} feedId={userdata.feedID} periodo={periodo} />}
      </div>
      {periodo.id !== 'todos' && onOpenEditor && (
        <button type="button" onClick={() => onOpenEditor(periodo.days)} className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline font-medium">
          <Pencil className="w-3.5 h-3.5" />
          Editar el video completo (con todas las fotos)
        </button>
      )}
    </div>
  );
};

export default TimelapsePorPeriodo;
